import { useState, useEffect, useContext } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const POLL_INTERVAL_MS = 60000; // 1 min — reduz requisições/CPU com muitos usuários (socket atualiza em tempo real)

const useTicketCounts = ({ selectedQueueIds, showAll, isRefreshing }) => {
  const { user } = useContext(AuthContext);
  const [ticketCounts, setTicketCounts] = useState({
    open: 0,
    pending: 0,
    group: 0,
    closed: 0,
    flow: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollTrigger, setPollTrigger] = useState(0);

  const isAdmin = user?.profile === "admin";
  const canSeeAllTickets = user?.allTicket === "enabled";
  const canSeeAllUserChats = user?.allUserChat === "enabled";
  const canAccessGroups = user?.allowGroup === true;
  const canSeeAllHistory = user?.allHistoric === "enabled";

  const effectiveShowAll =
    showAll &&
    (isAdmin || canSeeAllTickets || canSeeAllHistory || canSeeAllUserChats);

  const shouldIgnoreQueueFilter = effectiveShowAll;

  // Polling: atualiza contadores periodicamente para refletir mudanças (ex.: aba Aguardando) mesmo sem evento de socket
  useEffect(() => {
    const interval = setInterval(() => {
      setPollTrigger((t) => t + 1);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchTicketCounts = async () => {
      if (!isMounted) return;

      setLoading(true);
      setError(null);

      try {
        const validQueueIds = Array.isArray(selectedQueueIds)
          ? selectedQueueIds
          : [];

        let filteredQueueIds = validQueueIds;
        if (
          !isAdmin &&
          canSeeAllUserChats &&
          !canSeeAllHistory &&
          user?.queues
        ) {
          const userQueueIds = user.queues.map((q) => q.id);
          filteredQueueIds = validQueueIds.filter((id) =>
            userQueueIds.includes(id)
          );
        }

        const { data } = await api.get("/tickets/count", {
          params: {
            queueIds: JSON.stringify(filteredQueueIds),
            showAll: showAll ? "true" : "false",
          },
          timeout: 20000,
        });

        if (isMounted) {
          if (data && typeof data === "object") {
            const normalizedData = {
              open: parseInt(data.open) || 0,
              pending: parseInt(data.pending) || 0,
              group: parseInt(data.group) || 0,
              closed: parseInt(data.closed) || 0,
              flow: parseInt(data.flow) || 0,
            };

            if (!canAccessGroups) {
              normalizedData.group = 0;
            }

            setTicketCounts(normalizedData);
          } else {
            setError(new Error("Formato de dados inválido"));
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTicketCounts();

    return () => {
      isMounted = false;
    };
  }, [
    selectedQueueIds,
    showAll,
    isRefreshing,
    pollTrigger,
    canAccessGroups,
    effectiveShowAll,
    isAdmin,
    canSeeAllUserChats,
    canSeeAllHistory,
    user,
  ]);

  return {
    ticketCounts,
    loading,
    error,
    permissions: {
      isAdmin,
      canSeeAllTickets,
      canSeeAllUserChats,
      canAccessGroups,
      canSeeAllHistory,
      effectiveShowAll,
      shouldIgnoreQueueFilter,
    },
  };
};

export default useTicketCounts;

